import {
    DeskChatFeedbackDismissKey,
    type IDismissRequest,
    IssueMarkDismissKey,
    type IUndismissRequest,
} from '@kcftech/prometheus-client';
import { MissionControlCategoryIssueMarkTypes } from '@kcftech/prometheus-constants';
import { filterMapToSet, toMap, toSet } from 'utils/array';
import { type FollowUpQueueItem, type NeedsAttentionCard } from '../data/queueTypes';
import {
    getAssemblyName,
    getIssueQueueRow,
    type QueueRowKey,
} from '../queue/queueRowKey';

/** Everything to identify dismiss/undismiss target records for one queue row */
export type DismissTargets = Omit<IDismissRequest, 'RemindAfter'> &
    Pick<IUndismissRequest, 'CommentIssueIds'>;

export function getDismissRequest(
    { AlertIds, IssueMarks, CommentIds, DeskChatFeedback }: DismissTargets,
    remindAfter: Date | undefined,
): IDismissRequest {
    return {
        AlertIds,
        IssueMarks,
        CommentIds,
        DeskChatFeedback,
        RemindAfter: remindAfter,
    };
}

export function getUndismissRequest({
    AlertIds,
    IssueMarks,
    CommentIssueIds,
    DeskChatFeedback,
}: DismissTargets): IUndismissRequest {
    return { AlertIds, IssueMarks, CommentIssueIds, DeskChatFeedback };
}

/** Identifies a dismissable queue row. */
export type RowDismissalKey = Exclude<QueueRowKey, `closeout:${string}`>;

export type DismissableSection = 'needs-attention' | 'follow-up';

export function getRowDismissalSection(key: RowDismissalKey): DismissableSection {
    return key.startsWith('needs-attention:') ? 'needs-attention' : 'follow-up';
}

/** A queue row's dismissal info, as consumed by `RowActionsButton`. */
export type RowDismissal = Readonly<{
    key: RowDismissalKey;
    /** names the row in messages about it */
    label: string;
    /**
     * Needs Attention Issue Mark dismissals must expire, so rows with any can
     * only be dismissed with a "remind me later" time
     */
    canDismissIndefinitely: boolean;
    /** Throws if the row's data is inconsistent */
    getTargets: () => DismissTargets;
}>;

/** Dismisses every inclusion reason (trigger) on a Needs Attention card. */
export function getNeedsAttentionDismissal(card: NeedsAttentionCard): RowDismissal {
    return {
        key: `needs-attention:${card.AssemblyId}`,
        label: getAssemblyName(card),
        canDismissIndefinitely: card.InclusionReasons.every((r) => !r.IssueId),
        getTargets: () => getNeedsAttentionTargets(card),
    };
}

export function getNeedsAttentionTargets(card: NeedsAttentionCard): DismissTargets {
    const alertIds = new Set<string>();
    const issueIds = new Set<string>();
    for (const reason of card.InclusionReasons) {
        if (reason.AlertId) {
            alertIds.add(reason.AlertId);
        } else if (reason.IssueId) {
            issueIds.add(reason.IssueId);
        } else {
            throw new Error(
                `Assembly ${card.AssemblyId} has an inclusion reason with neither an Alert nor an Issue`,
            );
        }
    }

    const issuesById = toMap(card.Issues, (i) => i.Issue.Id);
    const issueMarks = Array.from(issueIds, (issueId) => {
        const item = issuesById.get(issueId);
        if (!item) {
            throw new Error(
                `Assembly ${card.AssemblyId} has an inclusion reason for Issue ${issueId}, which is not on the card`,
            );
        }
        // Needs Attention reasons only carry the Issue's id, so the triggering marks
        // are recovered from the card's Issue Marks of the category's types.
        const types = filterMapToSet(
            item.IssueMarks,
            (m) =>
                !m.ResolvedTime &&
                MissionControlCategoryIssueMarkTypes.NeedsAttention.has(m.Type),
            (m) => m.Type,
        );
        if (types.size === 0) {
            throw new Error(
                `Issue ${issueId} is a Needs Attention reason, but has no unresolved Needs Attention Issue Marks`,
            );
        }
        return new IssueMarkDismissKey({ IssueId: issueId, Types: [...types] });
    });

    return { AlertIds: [...alertIds], IssueMarks: issueMarks };
}

/** Dismisses every reason (Issue Marks, comments, feedback) on a Follow Up item. */
export function getFollowUpDismissal(item: FollowUpQueueItem): RowDismissal {
    return {
        key: `follow-up:${item.Issue.Id}`,
        label: getIssueQueueRow('followUp', item.Issue).label,
        // Follow Up Issue Marks mark one occurrence each, so they may be dismissed for good
        canDismissIndefinitely: true,
        getTargets: () => getFollowUpTargets(item),
    };
}

export function getFollowUpTargets(item: FollowUpQueueItem): DismissTargets {
    const markTypes = toSet(item.IssueMarks, (m) => m.Type);
    return {
        IssueMarks:
            markTypes.size > 0
                ? [
                      new IssueMarkDismissKey({
                          IssueId: item.Issue.Id,
                          Types: [...markTypes],
                      }),
                  ]
                : [],
        CommentIds: item.Comments.map((c) => c.Id),
        CommentIssueIds: item.Comments.length > 0 ? [item.Issue.Id] : [],
        DeskChatFeedback: item.Feedback.map(
            (f) =>
                new DeskChatFeedbackDismissKey({
                    ActivityId: f.ActivityId,
                    FeedbackUserId: f.User.UserId,
                }),
        ),
    };
}
